/* LuxeLane v2 motion + site behaviour (ported from luxlane-HTML).
 *
 * Progressive enhancement: <html> gets `lx-motion` from an inline head
 * script only when motion is allowed. Content hidden by .lx-reveal is
 * revealed here; if GSAP never loads, the failsafe removes `lx-motion`
 * so nothing stays invisible. prefers-reduced-motion skips all motion.
 */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FAILSAFE_MS = 3500;

  const failsafe = setTimeout(() => root.classList.remove('lx-motion'), FAILSAFE_MS);

  /* ---------- Cart (shared by product cards + PDP) ---------- */
  const cartCountEls = () => document.querySelectorAll('[data-lx-cart-count]');
  const routeRoot = () => window.Shopify?.routes?.root || '/';

  // Updates every cart bubble: number for 1-9, glowing blob over 9, hidden at 0.
  const BUMP_MS = 500;
  function setCartCount(count) {
    const n = Number(count) || 0;
    const state = n === 0 ? 'empty' : n > 9 ? 'many' : 'some';
    document.querySelectorAll('.lx-nav__cart').forEach((link) => link.classList.toggle('has-items', n > 0));
    cartCountEls().forEach((el) => {
      const changed = el.textContent.trim() !== String(n);
      el.textContent = n;
      el.dataset.state = state;
      el.setAttribute('aria-label', `${n} ${n === 1 ? 'item' : 'items'} in cart`);
      if (changed && n > 0) {
        el.classList.remove('is-bump');
        void el.offsetWidth; // restart the bump animation
        el.classList.add('is-bump');
        setTimeout(() => el.classList.remove('is-bump'), BUMP_MS);
      }
    });
  }

  async function refreshCartCount() {
    try {
      const res = await fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      setCartCount(cart.item_count);
    } catch (_) {
      /* count stays as rendered */
    }
  }

  // options.silent: skip the count refresh + lx:cart-updated (caller does it later)
  async function addToCart(id, quantity = 1, sellingPlan = null, options = {}) {
    const item = { id: Number(id), quantity: Number(quantity) };
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    const res = await fetch(`${routeRoot()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
    if (options.silent) return data;
    await refreshCartCount();
    document.dispatchEvent(new CustomEvent('lx:cart-updated'));
    return data;
  }

  /* Product image flies from the card and shrinks into the Cart badge. */
  const FLY_MS = 800;
  function flyToCart(img) {
    return new Promise((resolve) => {
      const target = document.querySelector('.lx-nav__cart');
      if (!img || !target || reduceMotion) return resolve();
      document.querySelector('[data-lx-nav]')?.classList.remove('is-hidden'); // bring the nav back
      const from = img.getBoundingClientRect();
      const to = target.getBoundingClientRect();
      const size = Math.min(from.width, from.height, 220);
      const ghost = img.cloneNode();
      ghost.removeAttribute('loading');
      ghost.removeAttribute('srcset');
      ghost.className = 'lx-fly';
      const left = from.left + (from.width - size) / 2;
      const top = from.top + (from.height - size) / 2;
      Object.assign(ghost.style, { left: `${left}px`, top: `${top}px`, width: `${size}px`, height: `${size}px` });
      document.body.append(ghost);
      // aim at the badge on the button's top-right corner
      const dx = to.right - 2 - (left + size / 2);
      const dy = to.top + 2 - (top + size / 2);
      ghost
        .animate(
          [
            { transform: 'translate(0, 0) scale(1)', borderRadius: '12px', opacity: 1 },
            { transform: `translate(${dx * 0.35}px, ${dy * 0.35 - 70}px) scale(0.7)`, borderRadius: '30%', opacity: 1, offset: 0.4 },
            { transform: `translate(${dx}px, ${dy}px) scale(0.07)`, borderRadius: '50%', opacity: 0.4 },
          ],
          { duration: FLY_MS, easing: 'cubic-bezier(0.5, 0, 0.2, 1)' }
        )
        .finished.catch(() => {})
        .finally(() => {
          ghost.remove();
          resolve();
        });
    });
  }
  const PHONE_MQ = '(max-width: 749px)';
  window.LxCart = { addToCart, refreshCartCount, setCount: setCartCount, flyToCart };

  // Quick-add "+" buttons on product cards. Without JS the form posts normally.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-lx-quick-add]');
    if (!form) return;
    event.preventDefault();
    const btn = form.querySelector('button');
    const id = form.querySelector('[name="id"]')?.value;
    if (!id || !btn) return;
    btn.disabled = true;
    try {
      // fly + request together; the badge pops once the image lands, then
      // (phones only) the drawer opens — desktop keeps the badge as feedback
      const fly = flyToCart(form.closest('.lx-prod')?.querySelector('.lx-prod__img img'));
      await addToCart(id, 1, null, { silent: true });
      await fly;
      await refreshCartCount();
      document.dispatchEvent(new CustomEvent('lx:cart-updated', { detail: { openDrawer: window.matchMedia(PHONE_MQ).matches } }));
      const label = btn.querySelector('[data-lx-add-label]');
      const icon = btn.querySelector('.lx-add__icon');
      btn.classList.add('is-added');
      if (label) label.textContent = 'Added';
      if (icon) icon.textContent = '✓';
      setTimeout(() => {
        btn.classList.remove('is-added');
        if (label) label.textContent = 'Add to cart';
        if (icon) icon.textContent = '+';
      }, 1400);
    } catch (error) {
      btn.title = error.message;
      form.submit();
    } finally {
      btn.disabled = false;
    }
  });

  /* ---------- Product cards on touch screens: like hover on desktop, but
     only ONE card at a time — the visible card closest to the centre of the
     screen (both directions, so 2-up grids and carousels work) gets its
     "Add to cart" button opened; it closes when another card takes over. ---------- */
  const CENTER_BAND = 0.35; // must sit within the middle 70% of the screen height
  if ('IntersectionObserver' in window && window.matchMedia('(hover: none)').matches) {
    const visible = new Set();
    let current = null;
    let ticking = false;

    const pickCard = () => {
      ticking = false;
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      let best = null;
      let bestDist = Infinity;
      visible.forEach((card) => {
        const r = card.getBoundingClientRect();
        const x = r.left + r.width / 2;
        const y = r.top + r.height / 2;
        if (Math.abs(y - cy) > window.innerHeight * CENTER_BAND) return;
        if (x < 0 || x > window.innerWidth) return;
        const dist = Math.hypot(x - cx, y - cy);
        if (dist < bestDist) {
          bestDist = dist;
          best = card;
        }
      });
      if (best === current) return;
      current?.classList.remove('is-inview');
      best?.classList.add('is-inview');
      current = best;
    };
    const schedule = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(pickCard);
    };

    const cardObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });
      schedule();
    });
    const watchCards = (scope) => scope.querySelectorAll?.('.lx-prod:not([data-lx-watched])').forEach((card) => {
      card.dataset.lxWatched = '1';
      cardObserver.observe(card);
    });
    // capture: also hears horizontal scrolling inside carousels
    document.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule, { passive: true });
    watchCards(document);
    // cards added later (lazy recommendations, theme editor reloads)
    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => node.nodeType === 1 && watchCards(node)));
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- Carousel arrows: [data-lx-row-prev|next] scroll the
     [data-lx-row] in the same section by one card. ---------- */
  const ROW_GAP_FALLBACK = 14;
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-lx-row-prev], [data-lx-row-next]');
    if (!btn) return;
    const row = btn.closest('section')?.querySelector('[data-lx-row]');
    const card = row?.firstElementChild;
    if (!row || !card) return;
    const gap = parseFloat(getComputedStyle(row).columnGap) || ROW_GAP_FALLBACK;
    const dir = btn.hasAttribute('data-lx-row-prev') ? -1 : 1;
    row.scrollBy({ left: dir * (card.getBoundingClientRect().width + gap), behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  /* ---------- Background videos ([data-lx-bgvideo] around lx-bg-media):
     keep the phone cut on phones, play only while on screen, start paused
     under reduced motion, and add a pause/play button. ---------- */
  const PHONE_QUERY = '(max-width: 749px)';
  function initBgVideos(scope = document) {
    scope.querySelectorAll('[data-lx-bgvideo]').forEach((box) => {
      if (box.dataset.lxBgReady) return;
      const videos = [...box.querySelectorAll('[data-lx-bg-video]')];
      if (!videos.length) return;
      box.dataset.lxBgReady = '1';
      const phone = window.matchMedia(PHONE_QUERY).matches;
      const video = videos.find((v) => v.dataset.lxBgVideo === (phone ? 'mobile' : 'desktop')) || videos[0];
      videos.filter((v) => v !== video).forEach((v) => v.remove());

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'lx-bgv-pause';
      btn.innerHTML = '<span aria-hidden="true"></span>';
      box.append(btn);

      let userPaused = reduceMotion;
      // optional loop limit: play N times, then stop on the last frame
      const loops = Number(video.dataset.lxLoops) || 0;
      let played = 0;
      let finished = false;
      if (loops > 0) {
        video.loop = false;
        video.addEventListener('ended', () => {
          played += 1;
          if (played < loops) {
            video.currentTime = 0;
            play();
          } else {
            finished = true;
            setLabel();
          }
        });
      }
      const setLabel = () => {
        btn.setAttribute('aria-pressed', String(video.paused));
        btn.setAttribute('aria-label', video.paused ? 'Play background video' : 'Pause background video');
      };
      const play = () => {
        if (userPaused || finished) return setLabel();
        video.preload = 'auto';
        const attempt = video.play();
        if (attempt) attempt.catch(() => {}).finally(setLabel);
      };
      video.addEventListener('playing', () => video.classList.add('is-playing'));
      video.addEventListener('play', setLabel);
      video.addEventListener('pause', setLabel);
      btn.addEventListener('click', () => {
        if (video.paused && finished) {
          // replay from the start with a fresh loop count
          finished = false;
          played = 0;
          video.currentTime = 0;
        }
        userPaused = !video.paused;
        if (userPaused) video.pause();
        else play();
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => (entry.isIntersecting ? play() : video.pause()), { threshold: 0.15 }).observe(box);
      } else {
        play();
      }
      setLabel();
    });
  }
  initBgVideos();
  document.addEventListener('shopify:section:load', (event) => initBgVideos(event.target));

  /* ---------- Menu links: the clicked link's underline fills and stays
     while the next page loads (the old current link lets go). ---------- */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.lx-nav__links a');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
    link.closest('.lx-nav__links').querySelectorAll('a').forEach((a) => a.classList.toggle('is-current', a === link));
  });
  // back/forward cache: restore the real current link
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) document.querySelectorAll('.lx-nav__links a.is-current').forEach((a) => a.classList.remove('is-current'));
  });

  /* ---------- Mobile menu ---------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-lx-menu-toggle]');
    const drawer = document.querySelector('[data-lx-menu]');
    if (!drawer) return;
    if (toggle) {
      const open = !drawer.classList.contains('is-open');
      drawer.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }
    if (drawer.classList.contains('is-open') && !event.target.closest('[data-lx-menu]')) {
      drawer.classList.remove('is-open');
      document.querySelector('[data-lx-menu-toggle]')?.setAttribute('aria-expanded', 'false');
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelector('[data-lx-menu].is-open')?.classList.remove('is-open');
  });

  /* ---------- Nav hide on scroll down ---------- */
  const NAV_HIDE_AFTER = 300;
  let lastY = window.scrollY;
  // Light glass + white text while the nav sits over a full-bleed hero.
  function updateNavTone(nav) {
    const hero = document.querySelector('[data-lx-hero]');
    const over = Boolean(hero) && hero.getBoundingClientRect().bottom > nav.offsetTop + nav.offsetHeight;
    nav.classList.toggle('is-over-hero', over);
  }
  function onScrollNav(y) {
    const nav = document.querySelector('[data-lx-nav]');
    if (!nav) return;
    updateNavTone(nav);
    const menuOpen = document.querySelector('[data-lx-menu].is-open');
    nav.classList.toggle('is-hidden', !menuOpen && y > lastY && y > NAV_HIDE_AFTER);
    lastY = y;
    document.dispatchEvent(new CustomEvent('lx:scroll', { detail: { y } }));
  }

  const navEl = document.querySelector('[data-lx-nav]');
  if (navEl) updateNavTone(navEl);

  /* ---------- Static fallbacks (no motion) ---------- */
  function setGauge(circle) {
    const pct = Math.min(1, Math.max(0, Number(circle.dataset.lxGauge) || 0));
    circle.style.strokeDashoffset = String(264 * (1 - pct));
  }

  function showStatic(scope = document) {
    scope.querySelectorAll('[data-lx-count]').forEach((el) => {
      el.textContent = Number(el.dataset.lxCount).toLocaleString();
    });
    scope.querySelectorAll('.lx-data__bar i, .lx-bar i').forEach((bar) => bar.classList.add('is-in'));
    scope.querySelectorAll('[data-lx-gauge]').forEach(setGauge);
  }

  /* ---------- Cursor ---------- */
  function initCursor() {
    const dot = document.querySelector('.lx-cursor-dot');
    const ring = document.querySelector('.lx-cursor-ring');
    if (!dot || !ring || !window.matchMedia('(hover: hover)').matches) return;
    let mx = innerWidth / 2;
    let my = innerHeight / 2;
    let rx = mx;
    let ry = my;
    addEventListener(
      'mousemove',
      (e) => {
        mx = e.clientX;
        my = e.clientY;
        root.classList.add('lx-cursor-on');
      },
      { passive: true }
    );
    document.addEventListener('mouseleave', () => root.classList.remove('lx-cursor-on'));
    (function loop() {
      rx += (mx - rx) * 0.14;
      ry += (my - ry) * 0.14;
      dot.style.transform = `translate(${mx}px,${my}px) translate(-50%,-50%)`;
      ring.style.transform = `translate(${rx}px,${ry}px) translate(-50%,-50%)`;
      requestAnimationFrame(loop);
    })();
    document.addEventListener('mouseover', (e) => {
      const hit = e.target.closest('a,button,[role="button"],label,select,input');
      document.body.classList.toggle('lx-cursor-hover', Boolean(hit));
    });
  }

  /* ---------- Page loader (snippets/lx-global 'loader'): run the hero
     intro once it has wiped away so the motion isn't hidden behind it. ---------- */
  function whenLoaded(callback) {
    if (window.LxLoaded || !document.getElementById('LxLoader')) return callback();
    document.addEventListener('lx:loaded', callback, { once: true });
  }

  // Nav intro (CSS reveal: logo, links, account / cart) once the page is ready
  whenLoaded(() => document.querySelector('[data-lx-nav]')?.classList.add('is-nav-in'));

  /* ---------- Statement word split ---------- */
  function splitWords(el) {
    if (el.dataset.lxSplit) return;
    el.dataset.lxSplit = '1';
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(' '));
              return;
            }
            const word = document.createElement('span');
            word.className = 'lx-word';
            const inner = document.createElement('span');
            inner.textContent = part;
            word.appendChild(inner);
            frag.appendChild(word);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && !child.classList.contains('lx-inline-img')) {
          walk(child);
        }
      });
    };
    walk(el);
  }

  /* ---------- Animations ---------- */
  function animate(gsap, ScrollTrigger, scope = document) {
    scope.querySelectorAll('.lx-reveal').forEach((el) => {
      gsap.to(el, { opacity: 1, y: 0, duration: 1.1, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 88%' } });
    });
    scope.querySelectorAll('.lx-reveal-clip').forEach((el) => {
      gsap.to(el, { clipPath: 'inset(0 0 0% 0)', duration: 1.4, ease: 'expo.out', scrollTrigger: { trigger: el, start: 'top 85%' } });
    });
    scope.querySelectorAll('[data-lx-parallax]').forEach((img) => {
      // Centred travel (-half -> +half) inside the image's overflow, capped
      // so the frame's background can never show at either edge.
      // data-lx-parallax-dir="rise": the image starts lowered and rises only
      // back to its resting place (+half -> 0), so the top of the photo
      // (e.g. heads) is never pushed out of the frame; only the bottom crops.
      const half = Math.min(Number(img.dataset.lxParallax) || 12, 16) / 2;
      const rise = img.dataset.lxParallaxDir === 'rise';
      gsap.fromTo(img, { yPercent: rise ? half : -half }, {
        yPercent: rise ? 0 : half,
        ease: 'none',
        scrollTrigger: { trigger: img.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });
    scope.querySelectorAll('[data-lx-count]').forEach((el) => {
      const target = Number(el.dataset.lxCount) || 0;
      const state = { v: 0 };
      gsap.to(state, {
        v: target,
        duration: 1.8,
        ease: 'power2.out',
        onUpdate: () => (el.textContent = Math.round(state.v).toLocaleString()),
        scrollTrigger: { trigger: el, start: 'top 92%', once: true },
      });
    });
    scope.querySelectorAll('.lx-data__bar i, .lx-bar i').forEach((bar) => {
      ScrollTrigger.create({ trigger: bar, start: 'top 92%', once: true, onEnter: () => bar.classList.add('is-in') });
    });
    scope.querySelectorAll('[data-lx-gauge]').forEach((circle) => {
      ScrollTrigger.create({ trigger: circle, start: 'top 90%', once: true, onEnter: () => setGauge(circle) });
    });
    scope.querySelectorAll('[data-lx-marquee]').forEach((track) => {
      if (!track.dataset.lxDup) {
        track.innerHTML += track.innerHTML;
        track.dataset.lxDup = '1';
      }
      gsap.to(track, { xPercent: -50, ease: 'none', duration: Number(track.dataset.lxMarquee) || 30, repeat: -1 });
    });
    scope.querySelectorAll('[data-lx-words]').forEach((heading) => {
      splitWords(heading);
      gsap.to(heading.querySelectorAll('.lx-word > span'), {
        y: 0,
        duration: 1,
        ease: 'expo.out',
        stagger: 0.04,
        scrollTrigger: { trigger: heading, start: 'top 82%' },
      });
    });
  }

  function heroIntro(gsap, scope = document) {
    scope.querySelectorAll('[data-lx-hero]').forEach((hero) => {
      gsap.to(hero.querySelectorAll('.lx-hero__line > span'), { y: 0, duration: 1.2, ease: 'expo.out', stagger: 0.12, delay: 0.1 });
      gsap.to(hero.querySelectorAll('.lx-hero__link, .lx-hero__cta'), { opacity: 1, y: 0, duration: 1, ease: 'expo.out', delay: 0.6, stagger: 0.12 });
      const media = hero.querySelectorAll('.lx-hero__img');
      if (media.length) gsap.fromTo(media, { scale: 1.15 }, { scale: 1, duration: 1.8, ease: 'expo.out' });
    });
  }

  /* ---------- Boot ---------- */
  function boot() {
    initCursor();
    const { gsap, ScrollTrigger, Lenis } = window;

    if (reduceMotion || !gsap || !ScrollTrigger) {
      clearTimeout(failsafe);
      root.classList.remove('lx-motion');
      showStatic();
      addEventListener('scroll', () => onScrollNav(scrollY), { passive: true });
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    clearTimeout(failsafe);

    if (Lenis && !root.classList.contains('shopify-design-mode')) {
      // lerp 0.1 + wheelMultiplier 1 = smooth but responsive (0.09 felt laggy).
      const lenis = new Lenis({ lerp: 0.1, smoothWheel: true, wheelMultiplier: 1, touchMultiplier: 1.4 });
      lenis.on('scroll', ScrollTrigger.update);
      lenis.on('scroll', (e) => onScrollNav(e.scroll));
      gsap.ticker.add((t) => lenis.raf(t * 1000));
      gsap.ticker.lagSmoothing(0);
      window.LxLenis = lenis;
    } else {
      addEventListener('scroll', () => onScrollNav(scrollY), { passive: true });
    }

    animate(gsap, ScrollTrigger);
    whenLoaded(() => heroIntro(gsap));

    // Theme editor: re-run motion for a re-rendered section.
    document.addEventListener('shopify:section:load', (e) => {
      animate(gsap, ScrollTrigger, e.target);
      heroIntro(gsap, e.target);
      ScrollTrigger.refresh();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
